import React, { useState, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import "./MdvPanel.css";

const LAYER_1 = [
  {
    key: "sujeto",
    label: "01 · Sujeto",
    fields: ["identidad", "estado", "presencia"],
  },
  {
    key: "escenario",
    label: "02 · Escenario",
    fields: ["tipo", "descripcion", "epoca", "escala", "detalle"],
  },
  {
    key: "atmosfera",
    label: "03 · Atmósfera",
    fields: ["ambiental", "tono_emocional", "correlato_fisico"],
  },
];

const LAYER_2 = [
  {
    key: "iluminacion",
    label: "04 · Iluminación",
    fields: ["fuente", "direccion", "calidad", "temperatura"],
  },
  {
    key: "paleta",
    label: "05 · Paleta",
    fields: ["dominante", "temperatura_global", "tratamiento", "color_sombras"],
  },
  {
    key: "texturas",
    label: "06 · Texturas",
    fields: ["materiales", "comportamiento", "micro_detalle"],
  },
  {
    key: "estilo",
    label: "07 · Estilo",
    fields: ["registro", "referencia", "periodo"],
  },
];

const LAYER_3 = [
  {
    key: "composicion",
    label: "08 · Composición",
    fields: ["regla", "plano", "angulo", "espacio_negativo"],
  },
  {
    key: "optica",
    label: "09 · Óptica",
    fields: ["focal", "apertura", "angulo_camara"],
  },
  {
    key: "formato",
    label: "Formato",
    fields: ["ratio", "width", "height"],
  },
];

function countFilledDimensions(mdv) {
  if (!mdv) return 0;
  const dims = ["sujeto","escenario","atmosfera","iluminacion","paleta","texturas","estilo","composicion","optica"];
  return dims.filter(key => {
    const obj = mdv[key];
    return obj && Object.values(obj).some(v => v !== null && v !== undefined && v !== "");
  }).length;
}

function DimBlock({ dim, mdv }) {
  const data = mdv?.[dim.key] || {};
  return (
    <div className="mdv-dim">
      <div className="mdv-dim-label">{dim.label}</div>
      {dim.fields.map(f => {
        const val = data[f];
        const isEmpty = val === null || val === undefined || val === "";
        return (
          <div key={f} className={`mdv-field ${isEmpty ? "mdv-field--null" : "mdv-field--filled"}`}>
            <span className="mdv-field-key">{f.replace(/_/g, " ")}</span>
            <span className="mdv-field-val">{isEmpty ? "null" : String(val)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function MdvPanel({ mdv, hashtag_id, open: openProp, onToggle, fullscreen }) {
  const { motor } = useApp();
  const motorLabel = motor === "flux2" ? "Flux 2 Dev" : "Juggernaut";
  const [localOpen, setLocalOpen] = useState(openProp ?? false);
  const isOpen = openProp !== undefined ? openProp : localOpen;

  const toggle = useCallback(() => {
    if (onToggle) onToggle(!isOpen);
    else setLocalOpen(v => !v);
  }, [isOpen, onToggle]);

  const copyJSON = useCallback(() => {
    if (!mdv) return;
    navigator.clipboard.writeText(JSON.stringify(mdv, null, 2)).catch(() => {});
  }, [mdv]);

  const filled = countFilledDimensions(mdv);

  return (
    <div className={`mdv-panel ${fullscreen ? "mdv-panel--fullscreen" : ""}`}>
      <div className="mdv-header" onClick={fullscreen ? undefined : toggle}>
        <span className="mdv-title">OBJETO MDV</span>
        {hashtag_id && <span className="mdv-hashtag">{hashtag_id}</span>}
        <div className="mdv-header-actions">
          {mdv && (
            <button
              className="mdv-copy-btn"
              onClick={e => { e.stopPropagation(); copyJSON(); }}
              title="Copiar JSON"
            >
              Copy JSON
            </button>
          )}
          {!fullscreen && <span className="mdv-chevron">{isOpen ? "▲" : "▼"}</span>}
        </div>
      </div>

      {(isOpen || fullscreen) && (
        <div className="mdv-body">
          <div className="mdv-columns">
            <div className="mdv-col">
              <div className="mdv-col-header">Capa 1 — Intención visual</div>
              {LAYER_1.map(d => <DimBlock key={d.key} dim={d} mdv={mdv} />)}
            </div>
            <div className="mdv-col">
              <div className="mdv-col-header">Capa 2 — Lenguaje visual</div>
              {LAYER_2.map(d => <DimBlock key={d.key} dim={d} mdv={mdv} />)}
            </div>
            <div className="mdv-col">
              <div className="mdv-col-header">Capa 3 — Ejecución técnica</div>
              {LAYER_3.map(d => <DimBlock key={d.key} dim={d} mdv={mdv} />)}
            </div>
          </div>
          <div className="mdv-footer">
            <span>origen: {mdv ? "generado" : "vacío"}</span>
            <span>dimensiones: {filled}/9</span>
            <span>motor: {motorLabel}</span>
          </div>
        </div>
      )}
    </div>
  );
}
